import type { CreateMeetupInput, MeetupDetail, MeetupMessage, MeetupSummary } from "@hanjan-shieo/contracts";
import { channel } from "./platform";
import { channelApiUrl } from "./channel";

type Envelope<T> = { ok:true; data:T } | { ok:false; error:{ code:string; message:string; retryable:boolean; details?:{ appealReference?:string; appealToken?:string } } };

export class MeetupApiError extends Error {
  constructor(public readonly code:string, message:string, public readonly status:number, public readonly retryable:boolean, public readonly appealReference?:string) { super(message); }
}

const tokenKey = "hs-meetup-token-v1";
export function meetupToken() { return sessionStorage.getItem(tokenKey) ?? ""; }
export function forgetMeetupToken() { sessionStorage.removeItem(tokenKey); }

async function request<T>(path:string, init:RequestInit = {}, auth = false): Promise<T> {
  const headers = new Headers(init.headers);
  if (init.body) headers.set("content-type", "application/json");
  if (auth) {
    const token = meetupToken();
    if (!token) throw new MeetupApiError("MEETUP_401", "모임 참여 전 본인확인이 필요해요.", 401, false);
    headers.set("authorization", `Bearer ${token}`);
  }
  let response: Response;
  try { response = await fetch(channelApiUrl(path), { ...init, headers }); }
  catch { throw new MeetupApiError("OFFLINE", "인터넷 연결을 확인한 뒤 다시 시도해 주세요.", 0, true); }
  const body = await response.json().catch(() => null) as Envelope<T> | null;
  if (!response.ok || !body?.ok) {
    const error = body && !body.ok ? body.error : { code:"MEETUP_503", message:"모임 서버에 연결하지 못했어요.", retryable:true };
    if (body && !body.ok && body.error.details?.appealToken) sessionStorage.setItem(tokenKey, body.error.details.appealToken);
    if (response.status === 401) forgetMeetupToken();
    throw new MeetupApiError(error.code, error.message, response.status, error.retryable, error.details?.appealReference);
  }
  return body.data;
}

export async function meetupAvailability() {
  const data = await request<{ productType:string; features:{ meetups:boolean } }>(`/api/v1/bootstrap?channel=${channel}&build=${encodeURIComponent(import.meta.env.VITE_BUILD_VERSION ?? "local")}`);
  return data.features.meetups;
}

export async function createLocalMeetupTestSession(subject:string, publicAlias:string) {
  const data = await request<{ token:string; expiresAt:string }>("/api/v1/meetup/test-session", { method:"POST", body:JSON.stringify({ subject, publicAlias }) });
  sessionStorage.setItem(tokenKey, data.token);
  return data;
}

export function listMeetups(area:string, date:string, intent?:string) {
  const query = new URLSearchParams({ area, date });
  if (intent) query.set("intent", intent);
  return request<{ meetups:MeetupSummary[] }>(`/api/v1/meetups?${query}`);
}

export function createMeetup(input:CreateMeetupInput) {
  return request<{ detail:MeetupDetail }>("/api/v1/meetups", { method:"POST", body:JSON.stringify(input) }, true);
}

export function getMeetup(id:string) { return request<{ detail:MeetupDetail }>(`/api/v1/meetups/${id}`, {}, Boolean(meetupToken())); }
export function myMeetups() { return request<{ meetups:MeetupDetail[] }>("/api/v1/meetups/mine", {}, true); }
export function meetupMessages(id:string) { return request<{ messages:MeetupMessage[]; revision:number; chatClosesAt:string }>(`/api/v1/meetups/${id}/messages`, {}, true); }

export function meetupAction<T>(id:string, action:string, body:unknown) {
  return request<T>(`/api/v1/meetups/${id}/${action}`, { method:"POST", body:JSON.stringify(body) }, true);
}

export function submitMeetupAppeal(appealReference:string) {
  return request<{ queued:boolean; appealId:string }>("/api/v1/meetup/appeals", { method:"POST", body:JSON.stringify({ appealReference, reason:"이용 제한 재검토를 요청합니다.", idempotencyKey:crypto.randomUUID() }) }, true);
}

export function meetupSocket(id:string, onUpdate:() => void, onStatus:(status:"connected"|"reconnecting"|"closed") => void) {
  const token = meetupToken();
  if (!token) return () => undefined;
  let stopped = false;
  let socket: WebSocket | null = null;
  let retry = 0;
  let timer = 0;
  const open = () => {
    if (stopped) return;
    const url = new URL(channelApiUrl(`/api/v1/meetups/${id}/socket`), location.origin);
    url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
    socket = new WebSocket(url, [`hsmeetup.${token}`]);
    socket.addEventListener("open", () => { retry = 0; onStatus("connected"); });
    socket.addEventListener("message", () => onUpdate());
    socket.addEventListener("close", () => {
      if (stopped) return;
      onStatus("reconnecting");
      timer = window.setTimeout(open, Math.min(10_000, 800 * 2 ** retry++));
    });
    socket.addEventListener("error", () => socket?.close());
  };
  open();
  return () => { stopped = true; clearTimeout(timer); socket?.close(1000, "화면을 닫았어요"); };
}

export type ModerationReport = {
  id:string; meetupId:string; reporterSubjectId:string; targetSubjectId:string; messageId:string|null;
  reason:string; detail:string; createdAt:string; status:"queued"|"reviewing"|"resolved"|"dismissed"; resolutionNote:string;
  violationConfirmedAt:string|null; evidenceDeleteAt:string|null; evidenceDeletedAt:string|null;
};
export type ModerationAppeal = { id:string; subjectId:string; appealReference:string; reason:string; createdAt:string; status:"queued"|"reviewing"|"resolved"|"dismissed"; resolutionNote:string; evidenceDeleteAt:string|null; evidenceDeletedAt:string|null };

async function moderationRequest<T>(path:string, key:string, init:RequestInit = {}) {
  const headers = new Headers(init.headers);
  headers.set("x-hs-moderation-key", key);
  if (init.body) headers.set("content-type", "application/json");
  const response = await fetch(channelApiUrl(path), { ...init, headers });
  const body = await response.json().catch(() => null) as Envelope<T> | null;
  if (!response.ok || !body?.ok) {
    const error = body && !body.ok ? body.error : { code:"MODERATION_503", message:"신고 운영 큐를 열지 못했어요.", retryable:true };
    throw new MeetupApiError(error.code, error.message, response.status, error.retryable);
  }
  return body.data;
}

export function listModerationReports(key:string) {
  return moderationRequest<{ reports:ModerationReport[] }>("/api/v1/moderation/reports", key);
}

export function listModerationAppeals(key:string) {
  return moderationRequest<{ appeals:ModerationAppeal[] }>("/api/v1/moderation/appeals", key);
}

export function updateModerationReport(key:string, reportId:string, input:{ status:"reviewing"|"resolved"|"dismissed"; resolutionNote:string; sanctionSubjectId?:string; sanction?:"temporary"|"permanent"; suspendedUntil?:string; retainEvidenceUntil?:string }) {
  return moderationRequest<{ updated:boolean }>(`/api/v1/moderation/reports/${reportId}`, key, { method:"POST", body:JSON.stringify(input) });
}

export function updateModerationAppeal(key:string, appealId:string, input:{ status:"reviewing"|"resolved"|"dismissed"; resolutionNote:string; unsuspend:boolean; retainEvidenceUntil?:string }) {
  return moderationRequest<{ updated:boolean }>(`/api/v1/moderation/appeals/${appealId}`, key, { method:"POST", body:JSON.stringify(input) });
}
